/**
 * Onboarding API — typed wrappers over the backend's existing registration and
 * approval endpoints.
 *
 * No backend change is involved. `/auth/complete-registration` and
 * `/auth/check-status` are the same endpoints BigxBookApp drives from
 * RegistrationScreen and WaitingApprovalScreen; both are marked
 * `@AllowsPendingOnboarding()` on the server, which is precisely why they are
 * reachable while the merchant is still PENDING and everything else is not.
 *
 * Both calls go through `http()`, so they carry the bearer token minted by
 * verify-otp. A brand-new merchant IS authenticated — it simply is not
 * approved, and OnboardingGuard rejects every other route with
 * `code: 'ONBOARDING_INCOMPLETE'` until it is.
 */
import { http, HttpError } from '@/services/httpClient';
import type { UserInfo } from '@/store/useAppStore';
import type { StoreSummary } from './authApi';

/**
 * The server's four onboarding states.
 *
 * PENDING          — user exists, registration form not yet submitted.
 * WAITING_APPROVAL — form submitted, a platform admin must review it.
 * APPROVED         — may use the product.
 * REJECTED         — refused; terminal without support intervention.
 */
export type OnboardingStatus = 'PENDING' | 'WAITING_APPROVAL' | 'APPROVED' | 'REJECTED';

export interface RegistrationInput {
  name: string;
  email?: string;
  businessName: string;
  businessType?: string;
  gstin?: string;
  pan?: string;
  /** Owner's personal number, when the merchant phone is a shared counter line. */
  ownerPhone?: string;
}

interface CompleteRegistrationResponse {
  success: boolean;
  user: Pick<UserInfo, 'id' | 'merchantId' | 'role' | 'name'> & {
    onboardingStatus: OnboardingStatus;
  };
  merchant: { id: string; businessName: string; onboardingStatus: OnboardingStatus };
}

export interface CheckStatusResponse {
  success: boolean;
  user: Pick<UserInfo, 'id' | 'merchantId' | 'role' | 'name'> & {
    onboardingStatus: OnboardingStatus;
  };
  merchant?: { id: string; businessName: string; onboardingStatus: OnboardingStatus };
  /** Populated once approved, so the app can go straight to a store. */
  stores?: StoreSummary[];
}

/**
 * Submits the registration form. Moves the merchant PENDING → WAITING_APPROVAL.
 *
 * Optional fields are omitted rather than sent empty: the DTO validates GSTIN
 * and PAN with regexes that an empty string fails, so sending `gstin: ''`
 * would be rejected as malformed rather than treated as "not provided".
 */
export async function completeRegistration(
  input: RegistrationInput,
): Promise<CompleteRegistrationResponse> {
  const body: Record<string, string> = {
    name: input.name.trim(),
    businessName: input.businessName.trim(),
  };
  const optional: [keyof RegistrationInput, string | undefined][] = [
    ['email', input.email],
    ['businessType', input.businessType],
    ['gstin', input.gstin],
    ['pan', input.pan],
    ['ownerPhone', input.ownerPhone],
  ];
  for (const [key, value] of optional) {
    const trimmed = value?.trim();
    if (trimmed) body[key] = key === 'gstin' || key === 'pan' ? trimmed.toUpperCase() : trimmed;
  }

  return http<CompleteRegistrationResponse>('/auth/complete-registration', {
    method: 'POST',
    body,
  });
}

/** Polls the approval decision. Also returns the store list once approved. */
export async function checkStatus(): Promise<CheckStatusResponse> {
  return http<CheckStatusResponse>('/auth/check-status', { method: 'GET' });
}

/**
 * Recognises the terminal account states, which do NOT arrive through
 * `check-status`.
 *
 * This is the one place the onboarding flow cannot simply read a status field.
 * `JwtStrategy` hard-blocks REJECTED and SUSPENDED merchants at authentication,
 * before any controller runs — so a rejected merchant's `check-status` call is
 * answered with 401 `ACCOUNT_REJECTED` rather than a body saying "REJECTED".
 * Refreshing does not rescue it either: `/auth/refresh` only re-checks
 * `isActive`, so it happily mints a new token that the next request rejects
 * exactly the same way.
 *
 * Without this, a merchant whose application was refused would sit on the
 * "under review" screen forever, re-polling a decision that had already been
 * made — the poll would swallow each 401 as a network blip.
 *
 * Returns the status to record locally, or null if this is an ordinary error.
 */
export function terminalStatusFromError(err: unknown): 'REJECTED' | 'SUSPENDED' | null {
  if (!(err instanceof HttpError) || err.status !== 401) return null;
  const code = (err.body as { code?: string } | null)?.code;
  if (code === 'ACCOUNT_REJECTED') return 'REJECTED';
  if (code === 'ACCOUNT_SUSPENDED') return 'SUSPENDED';
  return null;
}
